// types
import type { UserAccessGrant, UserAccessScopeType } from '@/types/domain/user-access'

/**
 * A dealership as returned by the permissions endpoint's resolved access list.
 */
export interface AccessDealership {
  id: number
  name: string
  type?: string | null
  orgId: number
  orgName?: string | null
  manufacturerId: number | null
  manufacturerName?: string | null
  region: string
  [key: string]: unknown
}

/** Region entry derived from the dealership list */
export interface AccessRegion {
  id: string
  name: string
}

/** Manufacturer entry derived from the dealership list */
export interface AccessManufacturer {
  id: number
  name: string
  regions: string[]
}

/** Org (legacy "group") entry derived from the dealership list */
export interface AccessOrg {
  id: number
  name: string
  regions: string[]
  manufacturers: number[]
}

/** The full set of client-side groupings derived from a flat dealership list */
export interface AccessGroupings {
  regions: AccessRegion[]
  manufacturers: AccessManufacturer[]
  groups: AccessOrg[]
}

/** Display names for the region codes used across the platform */
const REGION_NAMES: Record<string, string> = {
  gb: 'United Kingdom',
  us: 'United States',
  es: 'Spain',
  fr: 'France',
  de: 'Germany',
  mx: 'Mexico'
}

/**
 * Derive region / manufacturer / org groupings from a flat resolved dealership
 * list. Access grouping is a pure UI concern: the API only returns the
 * dealerships a user can access, and the FE builds the filter hierarchies.
 *
 * @param dealerships - Resolved dealership list from the permissions endpoint
 * @returns Groupings sorted alphabetically by name
 */
export function buildAccessGroupings(dealerships: AccessDealership[]): AccessGroupings {
  const regions = new Map<string, AccessRegion>()
  const manufacturers = new Map<number, AccessManufacturer>()
  const orgs = new Map<number, AccessOrg>()

  for (const dealership of dealerships) {
    const region = (dealership.region ?? '').toLowerCase()

    // Region: unique per country code
    if (region && !regions.has(region)) {
      regions.set(region, { id: region, name: REGION_NAMES[region] ?? region.toUpperCase() })
    }

    // Manufacturer: only franchised dealerships carry one
    if (dealership.manufacturerId !== null && dealership.manufacturerId !== undefined) {
      let manufacturer = manufacturers.get(dealership.manufacturerId)
      // First occurrence creates the entry
      if (!manufacturer) {
        manufacturer = {
          id: dealership.manufacturerId,
          name: dealership.manufacturerName ?? String(dealership.manufacturerId),
          regions: []
        }
        manufacturers.set(dealership.manufacturerId, manufacturer)
      }
      // Track every region the manufacturer operates in
      if (region && !manufacturer.regions.includes(region)) {
        manufacturer.regions.push(region)
      }
    }

    // Org: every dealership belongs to one
    let org = orgs.get(dealership.orgId)
    // First occurrence creates the entry
    if (!org) {
      org = {
        id: dealership.orgId,
        name: dealership.orgName ?? String(dealership.orgId),
        regions: [],
        manufacturers: []
      }
      orgs.set(dealership.orgId, org)
    }
    // Track the org's regions and manufacturers
    if (region && !org.regions.includes(region)) {
      org.regions.push(region)
    }
    if (
      dealership.manufacturerId !== null &&
      dealership.manufacturerId !== undefined &&
      !org.manufacturers.includes(dealership.manufacturerId)
    ) {
      org.manufacturers.push(dealership.manufacturerId)
    }
  }

  const byName = <T extends { name: string }>(a: T, b: T): number => a.name.localeCompare(b.name)

  return {
    regions: [...regions.values()].sort(byName),
    manufacturers: [...manufacturers.values()].sort(byName),
    groups: [...orgs.values()].sort(byName)
  }
}

/**
 * Resolve the dealership IDs a set of grants gives access to, mirroring the
 * API-side union resolution (used for client-side previews before saving).
 *
 * - `all` matches every dealership
 * - `org` matches dealerships in that org
 * - `manufacturer` matches franchised dealerships of that manufacturer,
 *   optionally narrowed to a region (brandless sites can never match)
 * - `dealership` matches that single dealership
 *
 * @param grants - The grants to resolve
 * @param dealerships - The dealership pool to resolve against
 * @returns Sorted, de-duplicated dealership IDs
 */
export function resolveDealershipIdsFromGrants(
  grants: Pick<UserAccessGrant, 'scopeType' | 'scopeId' | 'region'>[],
  dealerships: AccessDealership[]
): number[] {
  const ids = new Set<number>()

  for (const grant of grants) {
    for (const dealership of dealerships) {
      if (grantMatchesDealership(grant, dealership)) {
        ids.add(dealership.id)
      }
    }
  }

  return [...ids].sort((a, b) => a - b)
}

/**
 * Check whether a single grant gives access to a single dealership.
 *
 * @param grant - The grant to test
 * @param dealership - The dealership to test against
 * @returns True when the grant covers the dealership
 */
function grantMatchesDealership(
  grant: { scopeType: UserAccessScopeType; scopeId?: number | null; region?: string | null },
  dealership: AccessDealership
): boolean {
  switch (grant.scopeType) {
    case 'all':
      return true
    case 'org':
      return dealership.orgId === grant.scopeId
    case 'manufacturer':
      // Brandless (multi-brand / used supermarket) sites never match manufacturer grants
      if (dealership.manufacturerId === null || dealership.manufacturerId !== grant.scopeId) {
        return false
      }
      // A region qualifier narrows the grant to dealerships in that region
      return !grant.region || dealership.region.toLowerCase() === grant.region.toLowerCase()
    case 'dealership':
      return dealership.id === grant.scopeId
    default:
      return false
  }
}
